import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useShopping } from "../context/ShoppingContext";
import { FaStar, FaRegStar, FaHeart, FaRegHeart } from "react-icons/fa";
import {
  Box,
  Container,
  Grid,
  Heading,
  Text,
  Button,
  Image,
  VStack,
  HStack,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import { FaCheck, FaChevronRight, FaStarHalf } from "react-icons/fa";
import { useToast, SimpleGrid } from "@chakra-ui/react";
import materialsService from "../services/materialsService";

// Mock data - replace with API call
const MOCK_PRODUCT_DETAILS = {
  "modern-basin-mixer": {
    id: 1,
    name: "Modern Basin Mixer",
    series: "Elegance Series",
    price: "₹4,999",
    stockStatus: "in-stock",
    stockQuantity: 15,
    images: [
      "https://m.media-amazon.com/images/I/61-O10+QdeL._SL1500_.jpg",
      "https://m.media-amazon.com/images/I/61-O10+QdeL._SL1500_.jpg",
      "https://m.media-amazon.com/images/I/61-O10+QdeL._SL1500_.jpg",
    ],
    rating: 4.5,
    reviewCount: 128,
    reviews: [
      {
        id: 1,
        user: "John D.",
        rating: 5,
        date: "2023-10-15",
        comment: "Excellent quality and easy to install.",
      },
      {
        id: 2,
        user: "Sarah M.",
        rating: 4,
        date: "2023-10-10",
        comment: "Good product but installation instructions could be clearer.",
      },
      {
        id: 3,
        user: "Mike R.",
        rating: 5,
        date: "2023-10-05",
        comment: "Perfect finish and great water flow.",
      },
    ],
    relatedProducts: [
      {
        id: 2,
        name: "Wall Mounted Faucet",
        series: "Premium Series",
        price: "₹6,999",
        image: "https://m.media-amazon.com/images/I/61-O10+QdeL._SL1500_.jpg",
      },
      {
        id: 3,
        name: "Tall Basin Mixer",
        series: "Elegance Series",
        price: "₹5,499",
        image: "https://m.media-amazon.com/images/I/61-O10+QdeL._SL1500_.jpg",
      },
      {
        id: 4,
        name: "Kitchen Sink Mixer",
        series: "Premium Series",
        price: "₹7,999",
        image: "https://m.media-amazon.com/images/I/61-O10+QdeL._SL1500_.jpg",
      },
    ],
    description:
      "Contemporary design meets functionality in this premium basin mixer. Features a smooth single-lever operation and water-saving aerator.",
    features: [
      "Single-lever operation",
      "Water-saving aerator",
      "Chrome-plated brass construction",
      "Ceramic disc cartridge",
      "Easy installation",
    ],
    specifications: {
      Material: "Brass with Chrome Finish",
      "Installation Type": "Deck Mounted",
      "Operation Type": "Single Lever",
      "Flow Rate": "1.5 GPM",
      Warranty: "10 Years",
      Standards: "IS 15700:2005",
    },
    dimensions: {
      Height: "152mm",
      "Spout Reach": "112mm",
      "Base Diameter": "48mm",
    },
  },
  // Add more products
};

const ProductDetails = () => {
  const toast = useToast();
  const { subcategory, productId, materialId } = useParams();
  const navigate = useNavigate();
  const bg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const { addToCart, toggleWishlist, isInWishlist } = useShopping();

  // State for image gallery
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  const [apiProduct, setApiProduct] = useState(null);
  useEffect(() => {
    if (materialId) materialsService.getMaterialById(materialId).then((m) => setApiProduct({ ...m, id: m._id, price: `₹${Number(m.mrp || 0).toLocaleString("en-IN")}`, series: m.categoryName || "Material", stockStatus: Number(m.inventory?.unrestrictedQuantity || 0) > 0 ? "in-stock" : "out-of-stock", stockQuantity: m.inventory?.unrestrictedQuantity || 0, images: ["https://m.media-amazon.com/images/I/61-O10+QdeL._SL1500_.jpg"], rating: 0, reviewCount: 0, reviews: [], relatedProducts: [], description: m.shortName || m.name, features: [], specifications: { "Material Code": m.materialCode, "Material Type": m.materialType, "UOM": m.uomName, "Status": m.status }, dimensions: {} })).catch(() => setApiProduct(false));
  }, [materialId]);
  const product = materialId ? apiProduct : MOCK_PRODUCT_DETAILS[productId];
  if (materialId && apiProduct === null) return <Container py={8}><Text>Loading product...</Text></Container>;

  if (!product) {
    return (
      <Container maxW="1440px" py={8}>
        <Text>Product not found</Text>
        <Button onClick={() => navigate(-1)} mt={4}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Box bg={bg} minH="100vh">
      <Container maxW="1440px" py={8}>
        {/* Breadcrumb Navigation */}
        <Breadcrumb
          spacing="8px"
          separator={<FaChevronRight color="gray.500" />}
          mb={8}
        >
          <BreadcrumbItem>
            <BreadcrumbLink href="/categories">Categories</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink href={materialId ? "/categories" : "/categories/bath"}>{materialId ? "Categories" : "Bath"}</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink href={materialId ? `/categories/${product.categoryId}` : `/categories/bath/${subcategory}`}>
              {materialId ? product.categoryName : subcategory.charAt(0).toUpperCase() + subcategory.slice(1)}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>{product.name}</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8}>
          {/* Left Column - Images */}
          <Box bg={cardBg} p={6} borderRadius="lg" shadow="sm">
            <Box position="relative">
              <Image
                src={product.images[activeImageIndex]}
                alt={product.name}
                width="100%"
                height="auto"
                objectFit="cover"
                borderRadius="md"
                fallbackSrc="https://via.placeholder.com/800x600"
              />
              {product.stockStatus === "out-of-stock" && (
                <Box
                  position="absolute"
                  top="10px"
                  right="10px"
                  bg="red.500"
                  color="white"
                  px={3}
                  py={1}
                  borderRadius="md"
                >
                  Out of Stock
                </Box>
              )}
            </Box>
            <HStack mt={4} spacing={4} overflowX="auto" py={2}>
              {product.images.map((img, index) => (
                <Image
                  key={index}
                  src={img}
                  alt={`${product.name} view ${index + 1}`}
                  width="100px"
                  height="100px"
                  objectFit="cover"
                  borderRadius="md"
                  cursor="pointer"
                  opacity={index === activeImageIndex ? 1 : 0.6}
                  _hover={{ opacity: 1 }}
                  onClick={() => setActiveImageIndex(index)}
                  fallbackSrc="https://via.placeholder.com/100x100"
                />
              ))}
            </HStack>
          </Box>

          {/* Right Column - Product Details */}
          <VStack align="stretch" spacing={6}>
            <Box>
              <Badge colorScheme="blue" mb={2}>
                {product.series}
              </Badge>
              <Heading size="xl" mb={2}>
                {product.name}
              </Heading>

              {/* Rating Section */}
              <HStack spacing={2} mb={4}>
                <HStack spacing={1}>
                  {[...Array(5)].map((_, index) => {
                    const rating = Math.round(product.rating * 2) / 2;
                    return index < Math.floor(rating) ? (
                      <FaStar key={index} color="#FFD700" />
                    ) : index + 0.5 === rating ? (
                      <FaStarHalf key={index} color="#FFD700" />
                    ) : (
                      <FaRegStar key={index} color="#FFD700" />
                    );
                  })}
                </HStack>
                <Text color="gray.500">({product.reviewCount} reviews)</Text>
              </HStack>

              <Text fontSize="2xl" fontWeight="bold" color="blue.500" mb={2}>
                {product.price}
              </Text>

              {/* Stock Status */}
              <HStack mb={4}>
                <Badge
                  colorScheme={
                    product.stockStatus === "in-stock" ? "green" : "red"
                  }
                  fontSize="sm"
                  px={2}
                  py={1}
                >
                  {product.stockStatus === "in-stock"
                    ? `In Stock (${product.stockQuantity} available)`
                    : "Out of Stock"}
                </Badge>
              </HStack>

              <Text color="gray.600" fontSize="lg" mb={6}>
                {product.description}
              </Text>

              {/* Quantity Selector and Add to Cart */}
              <HStack spacing={4} mb={4}>
                <HStack>
                  <Button
                    size="sm"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    -
                  </Button>
                  <Text px={4}>{quantity}</Text>
                  <Button
                    size="sm"
                    onClick={() =>
                      setQuantity(
                        Math.min(product.stockQuantity || 10, quantity + 1)
                      )
                    }
                  >
                    +
                  </Button>
                </HStack>
              </HStack>

              <HStack spacing={4}>
                <Button
                  colorScheme="blue"
                  size="lg"
                  flex={1}
                  isDisabled={product.stockStatus !== "in-stock"}
                  onClick={() => {
                    addToCart({ ...product, quantity });
                    toast({
                      title: "Added to cart",
                      status: "success",
                      duration: 2000,
                      isClosable: true,
                    });
                  }}
                >
                  Add to Cart
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => toggleWishlist(product)}
                  leftIcon={
                    isInWishlist(product.id) ? <FaHeart /> : <FaRegHeart />
                  }
                >
                  {isInWishlist(product.id) ? "In Wishlist" : "Add to Wishlist"}
                </Button>
              </HStack>
            </Box>

            <Tabs isLazy>
              <TabList>
                <Tab>Features</Tab>
                <Tab>Specifications</Tab>
                <Tab>Dimensions</Tab>
                <Tab>Reviews ({product.reviews.length})</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <List spacing={3}>
                    {product.features.map((feature, index) => (
                      <ListItem key={index} display="flex" alignItems="center">
                        <ListIcon as={FaCheck} color="green.500" />
                        {feature}
                      </ListItem>
                    ))}
                  </List>
                </TabPanel>

                <TabPanel>
                  <VStack align="stretch" spacing={4}>
                    {Object.entries(product.specifications).map(
                      ([key, value]) => (
                        <HStack key={key} justify="space-between">
                          <Text fontWeight="bold">{key}</Text>
                          <Text>{value}</Text>
                        </HStack>
                      )
                    )}
                  </VStack>
                </TabPanel>

                <TabPanel>
                  <VStack align="stretch" spacing={4}>
                    {Object.entries(product.dimensions).map(([key, value]) => (
                      <HStack key={key} justify="space-between">
                        <Text fontWeight="bold">{key}</Text>
                        <Text>{value}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </TabPanel>

                <TabPanel>
                  <VStack align="stretch" spacing={6}>
                    {product.reviews.map((review) => (
                      <Box
                        key={review.id}
                        p={4}
                        borderWidth={1}
                        borderRadius="md"
                      >
                        <HStack mb={2} justify="space-between">
                          <Text fontWeight="bold">{review.user}</Text>
                          <Text color="gray.500" fontSize="sm">
                            {new Date(review.date).toLocaleDateString()}
                          </Text>
                        </HStack>
                        <HStack spacing={1} mb={2}>
                          {[...Array(5)].map((_, i) => (
                            <Box
                              key={i}
                              color={
                                i < review.rating ? "yellow.400" : "gray.300"
                              }
                            >
                              <FaStar />
                            </Box>
                          ))}
                        </HStack>
                        <Text>{review.comment}</Text>
                      </Box>
                    ))}
                  </VStack>
                </TabPanel>
              </TabPanels>

              {/* Related Products Section */}
              <Box mt={8}>
                <Heading size="md" mb={4}>
                  Related Products
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  {product.relatedProducts.map((relatedProduct) => (
                    <Box
                      key={relatedProduct.id}
                      p={4}
                      borderWidth={1}
                      borderRadius="lg"
                      cursor="pointer"
                      onClick={() =>
                        navigate(
                          `/categories/bath/${subcategory}/${relatedProduct.name
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`
                        )
                      }
                      _hover={{ shadow: "md" }}
                    >
                      <Image
                        src={relatedProduct.image}
                        alt={relatedProduct.name}
                        borderRadius="md"
                        mb={3}
                        fallbackSrc="https://via.placeholder.com/300x300"
                      />
                      <Text fontSize="sm" color="gray.500">
                        {relatedProduct.series}
                      </Text>
                      <Text fontWeight="semibold" mb={2}>
                        {relatedProduct.name}
                      </Text>
                      <Text color="blue.500" fontWeight="bold">
                        {relatedProduct.price}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            </Tabs>
          </VStack>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductDetails;
